"use client"

import { Download, CheckCircle, AlertCircle, ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TESLA_REQUIREMENTS } from '@/lib/audio-utils'
import { Spinner } from '@/components/ui/tls/spinner'
import { cardVariants } from '@/components/ui/card'
import Link from 'next/link'
import { PostDownloadPanel } from '@/components/ui/post-download-panel'

interface DownloadPanelProps {
  canProcess: boolean
  isProcessing: boolean
  hasProcessedAudio: boolean
  trimDuration: number
  onDownload: () => void  // Single handler that processes then downloads
  className?: string
  /** When set, show the full PostDownloadPanel after a successful export */
  soundId?: string
  soundTitle?: string
}

export function DownloadPanel({
  canProcess,
  isProcessing,
  hasProcessedAudio,
  trimDuration,
  onDownload,
  className,
  soundId,
  soundTitle,
}: DownloadPanelProps) {
  const isValidDuration = trimDuration <= TESLA_REQUIREMENTS.MAX_DURATION
  const isDisabled = !canProcess || isProcessing || !isValidDuration
  const accentInlineLinkClassName =
    "inline-flex items-center gap-1.5 text-sm text-[var(--tls-brand-ink)] underline-offset-4 transition-colors duration-[var(--motion-duration-standard)] ease-[var(--motion-ease-standard)] hover:text-[var(--tls-brand-ink-hover)] hover:underline"

  return (
    // Rule 1: a top hairline, content on the canvas. `is-active` used to fill
    // this block with --tlsr-surface-2 the moment the file was exportable,
    // which made the export step the one filled, bordered box in the editor.
    // Readiness is already carried by the button's own enabled state.
    <div
      className={cn(
        cardVariants({ surface: "refresh" }),
        "space-y-4 p-5",
        className
      )}
    >
      {/* Single action button — tlsr-pill with brand-blue active / disabled states */}
      <button
        type="button"
        onClick={isDisabled ? undefined : onDownload}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        className={cn(
          "tlsr-pill min-h-[48px] w-full justify-center",
          isProcessing && "cursor-wait",
          isDisabled && "cursor-not-allowed opacity-40",
          !isDisabled && "is-active"
        )}
        style={
          isProcessing
            ? { transition: "none" }
            : undefined
        }
      >
        {isProcessing ? (
          <>
            <Spinner size={14} color="currentColor" />
            <span>Preparing Download…</span>
          </>
        ) : hasProcessedAudio ? (
          <>
            <Download className="size-4" aria-hidden="true" />
            <span>Download Again</span>
          </>
        ) : (
          <>
            <Download className="size-4" aria-hidden="true" />
            <span>Download LockChime.wav</span>
          </>
        )}
      </button>

      {/* Duration warning — ink, not colour; the system has no semantic red (§19.8) */}
      {!isValidDuration && (
        <div
          className="flex items-start gap-2 border-t border-[color:var(--tlsr-line)] pt-3 text-sm text-[var(--tls-fg-strong)]"
          role="alert"
        >
          <AlertCircle className="mt-0.5 size-4 flex-shrink-0" aria-hidden="true" />
          <span className="font-medium">
            Trim your selection to {TESLA_REQUIREMENTS.MAX_DURATION} seconds or less to process
          </span>
        </div>
      )}

      {/* Success message — verdict carried by the check mark and the wording, not colour */}
      {hasProcessedAudio && !isProcessing && (
        <output
          className="flex items-start gap-3 border-t border-[color:var(--tlsr-line)] pt-3 text-[var(--tls-fg-strong)]"
        >
          <CheckCircle className="mt-0.5 size-5 flex-shrink-0" aria-hidden="true" />
          <div className="space-y-1">
            <p className="text-sm font-medium">
              Your Tesla lock sound is ready!
            </p>
            <p className="text-xs text-[var(--tlsr-fg-muted)]">
              Download the file and follow the installation guide to set it up on your Tesla.
            </p>
          </div>
        </output>
      )}

      {/* Post-download panel: guide bridge, self-report, newsletter, email-file */}
      {hasProcessedAudio && soundId && soundTitle && (
        <PostDownloadPanel
          soundId={soundId}
          soundTitle={soundTitle}
          surface="chime_studio"
        />
      )}

      {/* Installation instructions link (shown when no soundId — generic fallback) */}
      {hasProcessedAudio && !soundId && (
        <div className="text-center">
          <Link
            href="/guide"
            className={accentInlineLinkClassName}
          >
            <span>View Installation Guide</span>
            <ExternalLink className="size-3.5" aria-hidden="true" />
          </Link>
        </div>
      )}

      {/* Privacy note */}
      <p className="tlsr-chrome text-center" style={{ color: "var(--tlsr-fg-subtle)" }}>
        Processed in your browser. Nothing uploads.
      </p>
    </div>
  )
}
